import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Button, Stack, Pagination, Alert, Tabs, Tab, Grid, Card,
  CardContent, Switch, Divider, TextField, IconButton, alpha, Avatar,
  CircularProgress, FormControl, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip,
} from '@mui/material';
import {
  Visibility, Description, Refresh, Settings, Gavel, CheckCircle,
  Warning, Timer, Draw, Article, Add, Edit, Save, Close, ContentCopy,
  Download,
} from '@mui/icons-material';
import { adminAPI, settingsAPI, tenancyAPI } from '../../api/client';
import { integerOnly, decimalOnly } from '../../utils/inputHelpers';

const statusColors = { draft: 'default', pending_tenant: 'warning', pending_landlord: 'info', active: 'success', expired: 'error', terminated: 'error', disputed: 'error' };

const DEFAULT_TEMPLATE_SECTIONS = [
  { title: 'Parties', content: 'This Tenancy Agreement is made between:\n\nLandlord: {landlord_name}\nAddress: {property_address}\n\nTenant: {tenant_name}\nEmail: {tenant_email}\nPhone: {tenant_phone}' },
  { title: 'Property Details', content: 'Property: {property_title}\nAddress: {property_address}, {property_city}\nRoom Type: {room_type}\nRoom Number: {room_number}' },
  { title: 'Term of Tenancy', content: 'Start Date: {start_date}\nEnd Date: {end_date}\nDuration: {duration}\n\nThe tenancy shall commence on the start date and end on the end date unless terminated earlier in accordance with this agreement.' },
  { title: 'Rent and Payment', content: 'Monthly Rent: GH₵ {monthly_rent}\nSecurity Deposit: GH₵ {deposit_amount}\nPlatform Fee: Included in total amount\nPayment Due: {payment_due_day}st of each month\n\nRent shall be paid via the CampusNest platform using the approved payment methods.' },
  { title: 'Obligations of Tenant', content: '1. Pay rent on or before the due date each month.\n2. Keep the premises in good condition.\n3. Not make structural alterations without written consent.\n4. Not sublet or assign the premises.\n5. Comply with all property rules and regulations.\n6. Report any maintenance issues promptly.\n7. Use the property solely for residential purposes.' },
  { title: 'Obligations of Landlord', content: '1. Provide the premises in a habitable condition.\n2. Maintain structural integrity of the building.\n3. Ensure basic amenities are functional.\n4. Provide reasonable notice before property inspections.\n5. Return security deposit per the terms of this agreement.\n6. Address reported maintenance issues in a timely manner.' },
  { title: 'Termination', content: 'Notice Period: {notice_period} days\n\nEither party may terminate this agreement by giving written notice of at least {notice_period} days. Early termination without proper notice may result in forfeiture of the security deposit or applicable penalties.' },
  { title: 'Dispute Resolution', content: 'Any disputes arising from this agreement shall first be resolved through the CampusNest platform\'s dispute resolution mechanism. If unresolved, parties may seek mediation or legal remedies as appropriate under applicable law.' },
];

function AdminTenancyPage() {
  const [agreements, setAgreements] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [ts, setTs] = useState({
    auto_generate: 'true', require_digital_signature: 'true',
    min_term_months: '3', max_term_months: '24',
    auto_renew: 'false', renewal_notice_days: '30',
    late_fee_percent: '5', grace_period_days: '7',
    require_guarantor: 'false', deposit_months: '2',
  });

  // Template editor state
  const [templateDialog, setTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateSections, setTemplateSections] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([
    { name: 'Standard Tenancy Agreement', desc: 'Default template for all bookings', active: true, sections: DEFAULT_TEMPLATE_SECTIONS },
    { name: 'Short-Term Lease', desc: 'For stays under 6 months', active: true, sections: DEFAULT_TEMPLATE_SECTIONS },
    { name: 'Long-Term Lease', desc: 'For stays over 12 months', active: true, sections: DEFAULT_TEMPLATE_SECTIONS },
    { name: 'Room Sharing Agreement', desc: 'For shared room bookings', active: false, sections: DEFAULT_TEMPLATE_SECTIONS },
  ]);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [viewDialog, setViewDialog] = useState(false);
  const [viewAgreement, setViewAgreement] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const fetchAgreements = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.listTenancy(params);
      setAgreements(res.data.agreements || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAgreements(); }, [page, statusFilter]);
  useEffect(() => {
    settingsAPI.list({ category: 'tenancy' }).then(res => {
      if (Array.isArray(res.data)) {
        const m = {}; res.data.forEach(s => { m[s.key] = s.value; });
        setTs(p => ({ ...p, ...m }));
      }
    }).catch(() => {});
  }, []);

  const saveSetting = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setTs(p => ({ ...p, [key]: String(value) }));
      setSuccess('Setting updated');
    } catch (_) { setError('Failed to update'); }
  };

  const activeCount = agreements.filter(a => a.status === 'active').length;

  const handleViewAgreement = async (id) => {
    setViewLoading(true);
    setViewDialog(true);
    try {
      const res = await adminAPI.getTenancyDetail(id);
      setViewAgreement(res.data);
    } catch (err) {
      setError('Failed to load agreement details');
      setViewDialog(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleDownloadAgreement = (agreement) => {
    if (!agreement) return;
    const content = `TENANCY AGREEMENT\n${'='.repeat(50)}\nAgreement #: ${agreement.agreement_number}\n\nParties:\n  Tenant: ${agreement.tenant_name}\n  Landlord: ${agreement.landlord_name}\n\nProperty: ${agreement.property_title}\n\nTerm:\n  Start: ${agreement.start_date ? new Date(agreement.start_date).toLocaleDateString() : 'N/A'}\n  End: ${agreement.end_date ? new Date(agreement.end_date).toLocaleDateString() : 'N/A'}\n\nRent: GH₵ ${agreement.monthly_rent?.toLocaleString()}\nDeposit: GH₵ ${agreement.deposit_amount?.toLocaleString()}\n\n${'='.repeat(50)}\nTERMS AND CONDITIONS\n${'='.repeat(50)}\n\n${agreement.terms_and_conditions || 'No terms available'}\n\n${agreement.special_clauses ? `SPECIAL CLAUSES:\n${agreement.special_clauses}\n` : ''}\nSIGNATURES:\n  Tenant Signed: ${agreement.tenant_signed ? `Yes (${new Date(agreement.tenant_signed_at).toLocaleString()})` : 'No'}\n  Landlord Signed: ${agreement.landlord_signed ? `Yes (${new Date(agreement.landlord_signed_at).toLocaleString()})` : 'No'}\n\nStatus: ${agreement.status?.replace('_', ' ')?.toUpperCase()}\n`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tenancy-${agreement.agreement_number}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Tenancy Agreements
          </Typography>
          <Typography variant="body2" color="text.secondary">Manage agreements, templates, digital signing, and lifecycle</Typography>
        </Box>
        <IconButton onClick={fetchAgreements} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="All Agreements" icon={<Gavel sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Agreement Settings" icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Templates & Signing" icon={<Draw sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total', count: total, color: '#6366f1', icon: <Gavel /> },
              { label: 'Active', count: activeCount, color: '#10b981', icon: <CheckCircle /> },
              { label: 'Pending', count: agreements.filter(a => a.status?.includes('pending')).length, color: '#f59e0b', icon: <Timer /> },
              { label: 'Expired', count: agreements.filter(a => a.status === 'expired').length, color: '#ef4444', icon: <Warning /> },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Card sx={{ borderLeft: `4px solid ${item.color}` }}>
                  <CardContent sx={{ py: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 36, height: 36 }}>{item.icon}</Avatar>
                      <Box><Typography variant="body2" color="text.secondary">{item.label}</Typography><Typography variant="h5" fontWeight={800}>{item.count}</Typography></Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select value={statusFilter} displayEmpty onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                <MenuItem value="">All Statuses</MenuItem>
                {['draft','pending_tenant','pending_landlord','active','expired','terminated','disputed'].map(s =>
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_',' ')}</MenuItem>)}
              </Select>
            </FormControl>
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Agreement #</TableCell><TableCell>Tenant</TableCell><TableCell>Landlord</TableCell>
                <TableCell>Property</TableCell><TableCell>Duration</TableCell><TableCell>Rent</TableCell>
                <TableCell>Status</TableCell><TableCell>Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                : agreements.length === 0 ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No agreements found</TableCell></TableRow>
                : agreements.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell><Typography fontWeight={600} variant="body2">{a.agreement_number}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600}>{a.tenant_name || `${a.tenant?.first_name || ''} ${a.tenant?.last_name || ''}`.trim() || 'N/A'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600}>{a.landlord_name || `${a.landlord?.first_name || ''} ${a.landlord?.last_name || ''}`.trim() || 'N/A'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 160 }}>{a.property_title || a.property?.title || 'N/A'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{new Date(a.start_date).toLocaleDateString()} - {new Date(a.end_date).toLocaleDateString()}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600}>GH₵ {a.monthly_rent?.toLocaleString()}</Typography></TableCell>
                    <TableCell><Chip label={a.status?.replace('_',' ')} size="small" color={statusColors[a.status]||'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View Agreement">
                          <IconButton size="small" onClick={() => handleViewAgreement(a.id)}><Visibility fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Download Agreement">
                          <IconButton size="small" onClick={() => handleViewAgreement(a.id)}><Download fontSize="small" /></IconButton>
                        </Tooltip>
                        {a.document_url && <Tooltip title="Open Document"><IconButton size="small" href={a.document_url} target="_blank"><Description fontSize="small" /></IconButton></Tooltip>}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 15 && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><Pagination count={Math.ceil(total/15)} page={page} onChange={(e,v)=>setPage(v)} color="primary" /></Box>}
        </>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Agreement Lifecycle</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                {[
                  { key: 'auto_generate', label: 'Auto-Generate Agreements', desc: 'Create agreement on booking confirmation' },
                  { key: 'auto_renew', label: 'Auto-Renewal', desc: 'Automatically renew expiring agreements' },
                  { key: 'require_guarantor', label: 'Require Guarantor', desc: 'Tenants must provide a guarantor' },
                ].map(item => (
                  <Stack key={item.key} direction="row" justifyContent="space-between" alignItems="center">
                    <Box><Typography fontWeight={600}>{item.label}</Typography><Typography variant="body2" color="text.secondary">{item.desc}</Typography></Box>
                    <Switch checked={ts[item.key]==='true'} onChange={(e)=>saveSetting(item.key, e.target.checked?'true':'false')} />
                  </Stack>
                ))}
                {[
                  { key: 'min_term_months', label: 'Min Term (months)' },
                  { key: 'max_term_months', label: 'Max Term (months)' },
                  { key: 'renewal_notice_days', label: 'Renewal Notice (days)' },
                  { key: 'deposit_months', label: 'Security Deposit (months)' },
                ].map(item => (
                  <Box key={item.key}><Typography fontWeight={600} gutterBottom>{item.label}</Typography>
                    <TextField size="small" type="number" value={ts[item.key]}
                      onChange={(e)=>setTs(p=>({...p,[item.key]:e.target.value}))}
                      onKeyDown={integerOnly}
                      onBlur={(e)=>saveSetting(item.key, e.target.value)} sx={{ width: 120 }} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Payment & Penalties</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box><Typography fontWeight={600} gutterBottom>Late Fee (%)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Percentage charged on late rent payments</Typography>
                  <TextField size="small" type="number" value={ts.late_fee_percent}
                    onChange={(e)=>setTs(p=>({...p,late_fee_percent:e.target.value}))}
                    onKeyDown={decimalOnly}
                    onBlur={(e)=>saveSetting('late_fee_percent', e.target.value)} sx={{ width: 120 }} />
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Grace Period (days)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Days before late fee applies</Typography>
                  <TextField size="small" type="number" value={ts.grace_period_days}
                    onChange={(e)=>setTs(p=>({...p,grace_period_days:e.target.value}))}
                    onKeyDown={integerOnly}
                    onBlur={(e)=>saveSetting('grace_period_days', e.target.value)} sx={{ width: 120 }} />
                </Box>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography fontWeight={600} gutterBottom>Agreement Status Flow</Typography>
                  <Stack spacing={1}>
                    {['Draft → Pending Tenant → Pending Landlord → Active','Active → Expired (auto)','Active → Terminated (manual)','Any → Disputed (escalation)'].map((flow,i) => (
                      <Stack key={i} direction="row" alignItems="center" spacing={1}>
                        <CheckCircle color="success" sx={{ fontSize: 16 }} />
                        <Typography variant="body2">{flow}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Agreement Templates</Typography>
                <Button variant="contained" startIcon={<Add />} size="small" onClick={() => {
                  setEditingTemplate(null);
                  setTemplateName('');
                  setTemplateDesc('');
                  setTemplateSections(JSON.parse(JSON.stringify(DEFAULT_TEMPLATE_SECTIONS)));
                  setTemplateDialog(true);
                }}>Add Template</Button>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                {savedTemplates.map((t, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: alpha('#8b5cf6', 0.1), color: '#8b5cf6', width: 36, height: 36 }}><Article /></Avatar>
                        <Box>
                          <Typography fontWeight={600}>{t.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{t.desc}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">{t.sections?.length || 0} sections</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip title="Edit template">
                          <IconButton size="small" onClick={() => {
                            setEditingTemplate(i);
                            setTemplateName(t.name);
                            setTemplateDesc(t.desc);
                            setTemplateSections(JSON.parse(JSON.stringify(t.sections || DEFAULT_TEMPLATE_SECTIONS)));
                            setTemplateDialog(true);
                          }}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Preview">
                          <IconButton size="small" onClick={() => {
                            const preview = (t.sections || []).map(s => `== ${s.title} ==\n\n${s.content}`).join('\n\n---\n\n');
                            setPreviewContent(preview);
                            setPreviewDialog(true);
                          }}><Visibility fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate">
                          <IconButton size="small" onClick={() => {
                            setSavedTemplates(prev => [...prev, { ...t, name: `${t.name} (Copy)`, active: false }]);
                            setSuccess('Template duplicated');
                          }}><ContentCopy fontSize="small" /></IconButton>
                        </Tooltip>
                        <Switch checked={t.active} onChange={() => {
                          setSavedTemplates(prev => prev.map((tmpl, idx) => idx === i ? { ...tmpl, active: !tmpl.active } : tmpl));
                        }} />
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>

              {/* Placeholders Reference */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 3, bgcolor: alpha('#6366f1', 0.02) }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Available Placeholders</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Use these in template content. They will be replaced with actual data when generating agreements.
                </Typography>
                <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap>
                  {['{tenant_name}','{tenant_email}','{tenant_phone}','{landlord_name}','{property_title}','{property_address}','{property_city}','{room_type}','{room_number}','{start_date}','{end_date}','{duration}','{monthly_rent}','{deposit_amount}','{payment_due_day}','{notice_period}','{platform_fee}'].map(p => (
                    <Chip key={p} label={p} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', mb: 0.5 }} />
                  ))}
                </Stack>
              </Paper>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Digital Signing</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Require Digital Signature</Typography><Typography variant="body2" color="text.secondary">Both parties must sign electronically</Typography></Box>
                  <Switch checked={ts.require_digital_signature==='true'} onChange={(e)=>saveSetting('require_digital_signature', e.target.checked?'true':'false')} />
                </Stack>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography fontWeight={600} gutterBottom>Signing Workflow</Typography>
                  <Stack spacing={1}>
                    {['Agreement generated from template','Tenant reviews and signs','Landlord reviews and signs','Both signatures verified','Agreement activated'].map((step,i) => (
                      <Stack key={i} direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ bgcolor: alpha('#8b5cf6', 0.1), color: '#8b5cf6', width: 24, height: 24, fontSize: '0.7rem' }}>{i+1}</Avatar>
                        <Typography variant="body2">{step}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>

                {/* Push to backend */}
                <Button variant="outlined" fullWidth startIcon={<Save />} onClick={async () => {
                  try {
                    const activeTemplate = savedTemplates.find(t => t.active);
                    if (activeTemplate) {
                      await tenancyAPI.updateTemplate({ sections: activeTemplate.sections, name: activeTemplate.name });
                      setSuccess('Template synced to backend');
                    } else {
                      setError('No active template to sync');
                    }
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Failed to sync template');
                  }
                }}>
                  Sync Active Template to Backend
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Template Editor Dialog */}
      <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>{editingTemplate !== null ? 'Edit Template' : 'Create New Template'}</Typography>
            <Typography variant="body2" color="text.secondary">Configure sections and content for this agreement template</Typography>
          </Box>
          <IconButton onClick={() => setTemplateDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Template Name" value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g. Standard Tenancy Agreement" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Description" value={templateDesc}
                  onChange={(e) => setTemplateDesc(e.target.value)} placeholder="e.g. Default template for all bookings" />
              </Grid>
            </Grid>

            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={700}>Template Sections ({templateSections.length})</Typography>
              <Button size="small" startIcon={<Add />} onClick={() => setTemplateSections(prev => [...prev, { title: '', content: '' }])}>
                Add Section
              </Button>
            </Stack>

            {templateSections.map((section, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="primary">Section {idx + 1}</Typography>
                  <IconButton size="small" color="error" onClick={() => setTemplateSections(prev => prev.filter((_, i) => i !== idx))}>
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
                <TextField fullWidth size="small" label="Section Title" value={section.title} sx={{ mb: 1.5 }}
                  onChange={(e) => {
                    const updated = [...templateSections];
                    updated[idx] = { ...updated[idx], title: e.target.value };
                    setTemplateSections(updated);
                  }} />
                <TextField fullWidth multiline rows={4} label="Section Content" value={section.content}
                  onChange={(e) => {
                    const updated = [...templateSections];
                    updated[idx] = { ...updated[idx], content: e.target.value };
                    setTemplateSections(updated);
                  }}
                  helperText="Use {placeholder_name} for dynamic fields - see placeholders reference"
                  sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }} />
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTemplateDialog(false)}>Cancel</Button>
          <Button variant="outlined" onClick={() => {
            const preview = templateSections.map(s => `== ${s.title} ==\n\n${s.content}`).join('\n\n---\n\n');
            setPreviewContent(preview);
            setPreviewDialog(true);
          }} startIcon={<Visibility />}>Preview</Button>
          <Button variant="contained" startIcon={templateSaving ? <CircularProgress size={16} /> : <Save />}
            disabled={!templateName || templateSaving}
            onClick={async () => {
              setTemplateSaving(true);
              try {
                const templateData = { name: templateName, desc: templateDesc, sections: templateSections, active: true };
                if (editingTemplate !== null) {
                  setSavedTemplates(prev => prev.map((t, i) => i === editingTemplate ? templateData : t));
                } else {
                  setSavedTemplates(prev => [...prev, templateData]);
                }
                // Also sync to backend
                try {
                  await tenancyAPI.updateTemplate({ sections: templateSections, name: templateName });
                } catch (_) {}
                setSuccess(editingTemplate !== null ? 'Template updated!' : 'Template created!');
                setTemplateDialog(false);
              } catch (err) {
                setError('Failed to save template');
              } finally {
                setTemplateSaving(false);
              }
            }}>
            {templateSaving ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Agreement Dialog */}
      <Dialog open={viewDialog} onClose={() => { setViewDialog(false); setViewAgreement(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Tenancy Agreement</Typography>
            {viewAgreement && <Typography variant="body2" color="text.secondary">#{viewAgreement.agreement_number}</Typography>}
          </Box>
          <Stack direction="row" spacing={1}>
            {viewAgreement && (
              <Button size="small" variant="outlined" startIcon={<Download />} onClick={() => handleDownloadAgreement(viewAgreement)}>Download</Button>
            )}
            <IconButton onClick={() => { setViewDialog(false); setViewAgreement(null); }}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : viewAgreement ? (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Tenant</Typography>
                  <Typography fontWeight={600}>{viewAgreement.tenant_name}</Typography>
                  <Typography variant="body2">{viewAgreement.tenant?.email}</Typography>
                  <Typography variant="body2">{viewAgreement.tenant?.phone}</Typography>
                  <Typography variant="body2" color="text.secondary">{[viewAgreement.tenant?.address_line1, viewAgreement.tenant?.city, viewAgreement.tenant?.region].filter(Boolean).join(', ') || 'No address'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Landlord</Typography>
                  <Typography fontWeight={600}>{viewAgreement.landlord_name}</Typography>
                  <Typography variant="body2">{viewAgreement.landlord?.email}</Typography>
                  <Typography variant="body2">{viewAgreement.landlord?.phone}</Typography>
                  <Typography variant="body2" color="text.secondary">{[viewAgreement.landlord?.address_line1, viewAgreement.landlord?.city, viewAgreement.landlord?.region].filter(Boolean).join(', ') || 'No address'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Property</Typography>
                  <Typography fontWeight={600}>{viewAgreement.property_title}</Typography>
                  <Typography variant="body2">{viewAgreement.property?.address}</Typography>
                  <Typography variant="body2">{viewAgreement.property?.city}</Typography>
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Start Date</Typography><Typography fontWeight={600}>{viewAgreement.start_date ? new Date(viewAgreement.start_date).toLocaleDateString() : 'N/A'}</Typography></Grid>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">End Date</Typography><Typography fontWeight={600}>{viewAgreement.end_date ? new Date(viewAgreement.end_date).toLocaleDateString() : 'N/A'}</Typography></Grid>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Monthly Rent</Typography><Typography fontWeight={600}>GH₵ {viewAgreement.monthly_rent?.toLocaleString()}</Typography></Grid>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Deposit</Typography><Typography fontWeight={600}>GH₵ {viewAgreement.deposit_amount?.toLocaleString()}</Typography></Grid>
              </Grid>
              <Divider />
              <Stack direction="row" spacing={2}>
                <Chip label={`Tenant: ${viewAgreement.tenant_signed ? 'Signed' : 'Not Signed'}`} color={viewAgreement.tenant_signed ? 'success' : 'warning'} variant="outlined" />
                <Chip label={`Landlord: ${viewAgreement.landlord_signed ? 'Signed' : 'Not Signed'}`} color={viewAgreement.landlord_signed ? 'success' : 'warning'} variant="outlined" />
                <Chip label={viewAgreement.status?.replace('_',' ')} color={statusColors[viewAgreement.status]||'default'} sx={{ textTransform: 'capitalize' }} />
              </Stack>
              {viewAgreement.terms_and_conditions && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, maxHeight: 300, overflow: 'auto' }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Terms & Conditions</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewAgreement.terms_and_conditions}</Typography>
                </Paper>
              )}
              {viewAgreement.special_clauses && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Special Clauses</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewAgreement.special_clauses}</Typography>
                </Paper>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setViewDialog(false); setViewAgreement(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Template Preview</DialogTitle>
        <DialogContent dividers>
          <Paper sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, whiteSpace: 'pre-wrap', fontFamily: 'serif', lineHeight: 1.8, maxHeight: 500, overflow: 'auto' }}>
            <Typography variant="h5" fontWeight={700} align="center" gutterBottom>TENANCY AGREEMENT</Typography>
            <Typography variant="body2" align="center" color="text.secondary" gutterBottom>CampusNest Student Accommodation Platform</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{previewContent}</Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminTenancyPage;
